import * as THREE from 'three';
import { addBeam, addMesh, finishModel, lowPolyMaterial, type VectorTuple } from './modelHelpers';

const paleIce = lowPolyMaterial('pale-glacial-ice', 0x9ddce9, {
  roughness: 0.38,
  metalness: 0.04,
  emissive: 0x267c9b,
  emissiveIntensity: 0.24,
});
const deepIce = lowPolyMaterial('deep-blue-ice', 0x397f9d, {
  roughness: 0.32,
  metalness: 0.06,
  emissive: 0x174f72,
  emissiveIntensity: 0.32,
});
const whiteIce = lowPolyMaterial('wind-polished-ice', 0xdaf2f1, {
  roughness: 0.48,
  emissive: 0x6cb4c1,
  emissiveIntensity: 0.13,
});
const glacierSnow = lowPolyMaterial('glacier-powder', 0xf2faf8, {
  emissive: 0xc2e2df,
  emissiveIntensity: 0.08,
});
const cliffRock = lowPolyMaterial('icefall-cliff', 0x536e75, {
  roughness: 1,
  emissive: 0x263f47,
  emissiveIntensity: 0.12,
});
const rockShade = lowPolyMaterial('icefall-cliff-shadow', 0x394f58);
const luminousIce = lowPolyMaterial('luminous-icefall-blue', 0x78dff0, {
  roughness: 0.24,
  metalness: 0.07,
  emissive: 0x218caf,
  emissiveIntensity: 0.48,
});
const icefallWhite = lowPolyMaterial('icefall-white-glint', 0xd6ffff, {
  roughness: 0.3,
  metalness: 0.04,
  emissive: 0x66c9db,
  emissiveIntensity: 0.3,
});
const sledCoral = lowPolyMaterial('survey-sled-coral', 0xc85f43, {
  emissive: 0x742819,
  emissiveIntensity: 0.08,
});
const sledSteel = lowPolyMaterial('survey-sled-steel', 0x405b63, {
  roughness: 0.62,
  metalness: 0.34,
});
const surveyWood = lowPolyMaterial('survey-crate-wood', 0x97734d);
const surveyWoodDark = lowPolyMaterial('survey-crate-shadow', 0x554636);
const surveyCanvas = lowPolyMaterial('survey-canvas-roll', 0x315f65);
const surveyRope = lowPolyMaterial('survey-lashing-rope', 0xd0a25f);
const mapPaper = lowPolyMaterial('survey-map-paper', 0xece5c8, {
  emissive: 0xc5b98b,
  emissiveIntensity: 0.06,
});
const mapInk = lowPolyMaterial('survey-map-ink', 0x315b65);
const ermineWhite = lowPolyMaterial('winter-ermine-fur', 0xe3e0d3, {
  emissive: 0xb8d3d3,
  emissiveIntensity: 0.05,
});
const ermineShade = lowPolyMaterial('winter-ermine-shadow-fur', 0xa9c1c3);
const ermineCharcoal = lowPolyMaterial('ermine-eyes-and-tail-tip', 0x26363a, {
  emissive: 0x101b20,
  emissiveIntensity: 0.1,
});
const ermineInnerEar = lowPolyMaterial('ermine-inner-ear', 0xd9968e);
const stolenMitten = lowPolyMaterial('stolen-survey-mitten', 0xd56149, {
  emissive: 0x7a291d,
  emissiveIntensity: 0.08,
});
const skidBlue = lowPolyMaterial('ermine-skid-blue', 0x65bbcf, {
  emissive: 0x277d98,
  emissiveIntensity: 0.2,
});

interface ArchBlock {
  readonly start: VectorTuple;
  readonly end: VectorTuple;
  readonly radius: number;
}

export function createIceArchModel(): THREE.Group {
  const group = new THREE.Group();
  const blocks: readonly ArchBlock[] = [
    { start: [-4.35, 0, 0.1], end: [-4.12, 2.35, -0.15], radius: 1.08 },
    { start: [-4.12, 2.15, -0.12], end: [-3.28, 4.25, 0.12], radius: 1.04 },
    { start: [-3.3, 4.08, 0.1], end: [-1.78, 5.75, -0.09], radius: 1 },
    { start: [-1.84, 5.65, -0.08], end: [0, 6.28, 0.1], radius: 0.96 },
    { start: [0, 6.28, 0.1], end: [1.88, 5.62, -0.12], radius: 0.96 },
    { start: [1.8, 5.7, -0.1], end: [3.35, 4.02, 0.16], radius: 1.02 },
    { start: [3.28, 4.15, 0.14], end: [4.18, 2.05, -0.16], radius: 1.05 },
    { start: [4.18, 2.18, -0.14], end: [4.42, 0, 0.08], radius: 1.09 },
  ];

  blocks.forEach((block, index) => {
    addBeam(
      group,
      `arch-ice-block-${index}`,
      block.start,
      block.end,
      block.radius,
      index % 3 === 1 ? deepIce : paleIce,
      block.radius * (index === 0 || index === blocks.length - 1 ? 1.05 : 0.88),
      6,
    );
  });

  for (const [index, x] of [-3.72, -2.48, -0.92, 0.82, 2.38, 3.68].entries()) {
    const y = 5.9 - Math.abs(x) * 0.65;
    addMesh(group, `arch-snow-crown-${index}`, new THREE.IcosahedronGeometry(0.75, 1), glacierSnow, {
      position: [x, y + 0.73, -0.03],
      scale: [1.18, 0.28, 1.42],
      rotation: [0, index * 0.38, x * -0.08],
    });
  }

  for (const [index, x] of [-3.82, 3.84].entries()) {
    addMesh(group, `arch-foot-drift-${index}`, new THREE.IcosahedronGeometry(1, 1), glacierSnow, {
      position: [x, 0.18, 0.05],
      scale: [1.8, 0.35, 1.55],
    });
    addMesh(group, `arch-side-crystal-${index}`, new THREE.OctahedronGeometry(0.56, 0), whiteIce, {
      position: [x + (index === 0 ? -1.05 : 1.05), 1.08, 0.28],
      rotation: [index === 0 ? -0.2 : 0.2, 0, index === 0 ? -0.38 : 0.38],
      scale: [0.7, 2.35, 0.72],
    });
  }
  return finishModel('ice-arch', 'Blue Ice Passage', 'ice-garden', group);
}

interface CrystalSpec {
  readonly angle: number;
  readonly distance: number;
  readonly height: number;
  readonly radius: number;
  readonly lean: number;
  readonly lateral: number;
  readonly dark?: boolean;
  readonly white?: boolean;
}

/**
 * A grounded crystal prism whose point leans without rotating its base away
 * from the snow. Keeping the lean in the vertices also prevents neighbouring
 * shards from intersecting at the root.
 */
function createCrystalShardGeometry(
  height: number,
  radius: number,
  lean: number,
  lateral: number,
  facetOffset: number,
): THREE.BufferGeometry {
  const facets = 5;
  const positions: number[] = [];
  const base: THREE.Vector3[] = [];
  const shoulder: THREE.Vector3[] = [];

  for (let index = 0; index < facets; index += 1) {
    const angle = facetOffset + index / facets * Math.PI * 2;
    base.push(new THREE.Vector3(Math.cos(angle) * radius, 0, Math.sin(angle) * radius));
    shoulder.push(new THREE.Vector3(
      Math.cos(angle) * radius * 0.72 + lean * 0.68,
      height * 0.76,
      Math.sin(angle) * radius * 0.72 + lateral * 0.68,
    ));
  }

  const tip = new THREE.Vector3(lean, height, lateral);
  const baseCenter = new THREE.Vector3(0, 0, 0);
  const pushTriangle = (a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3): void => {
    positions.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
  };

  for (let index = 0; index < facets; index += 1) {
    const next = (index + 1) % facets;
    pushTriangle(base[index]!, base[next]!, shoulder[index]!);
    pushTriangle(base[next]!, shoulder[next]!, shoulder[index]!);
    pushTriangle(shoulder[index]!, shoulder[next]!, tip);
    pushTriangle(baseCenter, base[next]!, base[index]!);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

export function createCrystalFanModel(): THREE.Group {
  const group = new THREE.Group();
  const crystals: readonly CrystalSpec[] = [
    // The hero shard anchors the composition without sharing geometry with
    // the surrounding ring.
    { angle: 0, distance: 0, height: 5.9, radius: 0.48, lean: 0.24, lateral: -0.08, dark: true },
    { angle: 0.18, distance: 1.32, height: 4.55, radius: 0.34, lean: 0.9, lateral: 0.12 },
    { angle: 1.08, distance: 1.66, height: 3.1, radius: 0.29, lean: 0.72, lateral: -0.08, white: true },
    { angle: 2.02, distance: 1.48, height: 4.05, radius: 0.33, lean: 0.84, lateral: 0.16 },
    { angle: 2.94, distance: 1.82, height: 2.45, radius: 0.27, lean: 0.68, lateral: -0.12, dark: true },
    { angle: 3.82, distance: 1.55, height: 3.55, radius: 0.31, lean: 0.8, lateral: 0.1 },
    { angle: 4.72, distance: 1.9, height: 2.72, radius: 0.27, lean: 0.74, lateral: -0.1, white: true },
    { angle: 5.55, distance: 1.58, height: 3.72, radius: 0.32, lean: 0.88, lateral: 0.14, dark: true },
    { angle: 6.02, distance: 2.22, height: 2.15, radius: 0.24, lean: 0.62, lateral: -0.08 },
  ];
  crystals.forEach((crystal, index) => {
    const directionX = Math.cos(crystal.angle);
    const directionZ = Math.sin(crystal.angle);
    const material = crystal.white ? whiteIce : crystal.dark ? deepIce : paleIce;
    addMesh(
      group,
      `fan-crystal-${index}`,
      createCrystalShardGeometry(
        crystal.height,
        crystal.radius,
        crystal.lean,
        crystal.lateral,
        index * 0.37,
      ),
      material,
      {
        position: [directionX * crystal.distance, 0, directionZ * crystal.distance],
        rotation: [0, -crystal.angle, 0],
      },
    );

    // Separate, irregular collars seat each root without turning the whole
    // sculpture into the flat oval shelf seen in the earlier model.
    addMesh(group, `fan-root-snow-${index}`, new THREE.IcosahedronGeometry(1, 0), glacierSnow, {
      position: [directionX * crystal.distance, 0.06, directionZ * crystal.distance],
      scale: [crystal.radius * 1.6, 0.09, crystal.radius * 1.38],
      rotation: [0, crystal.angle + index * 0.31, 0],
    });
  });
  return finishModel('crystal-fan', 'Glacial Crystal Fan', 'ice-garden', group);
}

function distortedRockGeometry(radius: number, seedOffset: number): THREE.BufferGeometry {
  const geometry = new THREE.DodecahedronGeometry(radius, 0);
  const positions = geometry.getAttribute('position') as THREE.BufferAttribute;
  for (let index = 0; index < positions.count; index += 1) {
    const x = positions.getX(index);
    const y = positions.getY(index);
    const z = positions.getZ(index);
    const variation = 0.9 + ((index * 17 + seedOffset * 13) % 11) / 55;
    positions.setXYZ(index, x * variation, y * (1.03 + (index % 3) * 0.025), z * (2 - variation));
  }
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function createIcicleGeometry(
  length: number,
  radius: number,
  bendX: number,
  bendZ: number,
  facetOffset: number,
): THREE.BufferGeometry {
  const facets = 6;
  const positions: number[] = [];
  const top: THREE.Vector3[] = [];
  const waist: THREE.Vector3[] = [];
  for (let index = 0; index < facets; index += 1) {
    const angle = facetOffset + index / facets * Math.PI * 2;
    top.push(new THREE.Vector3(Math.cos(angle) * radius, 0, Math.sin(angle) * radius));
    waist.push(new THREE.Vector3(
      Math.cos(angle) * radius * 0.52 + bendX * 0.48,
      -length * 0.58,
      Math.sin(angle) * radius * 0.52 + bendZ * 0.48,
    ));
  }
  const tip = new THREE.Vector3(bendX, -length, bendZ);
  const topCenter = new THREE.Vector3(0, 0, 0);
  const pushTriangle = (a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3): void => {
    positions.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
  };
  for (let index = 0; index < facets; index += 1) {
    const next = (index + 1) % facets;
    pushTriangle(top[index]!, waist[index]!, top[next]!);
    pushTriangle(top[next]!, waist[index]!, waist[next]!);
    pushTriangle(waist[index]!, tip, waist[next]!);
    pushTriangle(topCenter, top[next]!, top[index]!);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

/** A broad, folded triangular prism behind the individual icicle points. */
function createIceCurtainGeometry(
  width: number,
  length: number,
  depth: number,
  lowerSkew: number,
): THREE.BufferGeometry {
  const halfWidth = width / 2;
  const halfDepth = depth / 2;
  const frontLeft = new THREE.Vector3(-halfWidth, 0, halfDepth);
  const frontRight = new THREE.Vector3(halfWidth, 0, halfDepth);
  const frontTip = new THREE.Vector3(lowerSkew, -length, halfDepth * 0.22);
  const backLeft = new THREE.Vector3(-halfWidth, 0, -halfDepth);
  const backRight = new THREE.Vector3(halfWidth, 0, -halfDepth);
  const backTip = new THREE.Vector3(lowerSkew, -length, -halfDepth * 0.22);
  const positions: number[] = [];
  const pushTriangle = (a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3): void => {
    positions.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
  };

  pushTriangle(frontLeft, frontRight, frontTip);
  pushTriangle(backRight, backLeft, backTip);
  pushTriangle(frontLeft, backLeft, frontRight);
  pushTriangle(frontRight, backLeft, backRight);
  pushTriangle(frontRight, backRight, frontTip);
  pushTriangle(frontTip, backRight, backTip);
  pushTriangle(frontTip, backTip, frontLeft);
  pushTriangle(frontLeft, backTip, backLeft);

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

export function createFrozenIcefallModel(): THREE.Group {
  const group = new THREE.Group();
  const cliffs = [
    { x: -3.65, y: 2.85, z: -0.7, sx: 1.72, sy: 2.95, sz: 1.38, rz: -0.13 },
    { x: -1.85, y: 3.55, z: -1.02, sx: 2.02, sy: 3.68, sz: 1.48, rz: 0.08 },
    { x: 0.15, y: 4.05, z: -1.18, sx: 2.18, sy: 4.08, sz: 1.55, rz: -0.04 },
    { x: 2.18, y: 3.42, z: -0.96, sx: 1.92, sy: 3.5, sz: 1.42, rz: 0.1 },
    { x: 3.82, y: 2.62, z: -0.62, sx: 1.48, sy: 2.72, sz: 1.3, rz: 0.16 },
  ] as const;
  cliffs.forEach((cliff, index) => {
    addMesh(group, `icefall-rock-column-${index}`, distortedRockGeometry(1, index), index % 2 ? rockShade : cliffRock, {
      position: [cliff.x, cliff.y, cliff.z],
      scale: [cliff.sx, cliff.sy, cliff.sz],
      rotation: [0.03 * index, 0.29 * index - 0.4, cliff.rz],
    });
  });

  // Three rough chunks form a projecting cornice. Their front edges sit well
  // ahead of the supporting cliff, leaving a readable shadowed undercut in
  // side view instead of a waterfall pasted directly onto a wall.
  const ledgeChunks = [
    { x: -3.15, y: 6.2, z: 0.42, sx: 2.35, sy: 0.72, sz: 2.08, ry: -0.16 },
    { x: -0.15, y: 6.55, z: 0.58, sx: 2.55, sy: 0.78, sz: 2.28, ry: 0.12 },
    { x: 3.05, y: 6.02, z: 0.48, sx: 2.32, sy: 0.7, sz: 2.02, ry: 0.24 },
  ] as const;
  ledgeChunks.forEach((ledge, index) => {
    addMesh(group, `icefall-overhang-${index}`, distortedRockGeometry(1, 20 + index), index === 1 ? rockShade : cliffRock, {
      position: [ledge.x, ledge.y, ledge.z],
      scale: [ledge.sx, ledge.sy, ledge.sz],
      rotation: [0, ledge.ry, index === 0 ? -0.08 : index === 2 ? 0.07 : 0],
    });
  });

  const curtains = [
    { x: -2.65, y: 5.98, z: 1.62, width: 2.15, length: 4.18, depth: 0.38, skew: -0.32 },
    { x: -0.38, y: 6.2, z: 1.78, width: 2.35, length: 5.42, depth: 0.42, skew: 0.24 },
    { x: 2.08, y: 5.84, z: 1.61, width: 2.18, length: 4.66, depth: 0.36, skew: 0.36 },
  ] as const;
  curtains.forEach((curtain, index) => {
    addMesh(group, `icefall-curtain-${index}`, createIceCurtainGeometry(
      curtain.width,
      curtain.length,
      curtain.depth,
      curtain.skew,
    ), index === 1 ? icefallWhite : luminousIce, {
      position: [curtain.x, curtain.y, curtain.z],
      rotation: [0, index === 0 ? -0.05 : index === 2 ? 0.06 : 0, 0],
    });
  });

  const icicles = [
    { x: -4.48, y: 5.65, z: 1.25, length: 2.82, radius: 0.3, bendX: -0.28, bendZ: 0.12 },
    { x: -3.8, y: 5.92, z: 1.55, length: 3.72, radius: 0.42, bendX: -0.18, bendZ: 0.2 },
    { x: -3.1, y: 6.02, z: 1.8, length: 4.7, radius: 0.34, bendX: 0.16, bendZ: 0.12 },
    { x: -2.22, y: 6.12, z: 1.92, length: 3.58, radius: 0.38, bendX: -0.1, bendZ: 0.18 },
    { x: -1.35, y: 6.25, z: 1.86, length: 4.82, radius: 0.31, bendX: -0.22, bendZ: 0.08 },
    { x: -0.45, y: 6.28, z: 2.02, length: 5.58, radius: 0.43, bendX: 0.2, bendZ: 0.15 },
    { x: 0.52, y: 6.24, z: 1.96, length: 4.42, radius: 0.32, bendX: 0.13, bendZ: 0.2 },
    { x: 1.38, y: 6.06, z: 1.84, length: 5.08, radius: 0.4, bendX: -0.15, bendZ: 0.12 },
    { x: 2.26, y: 5.92, z: 1.82, length: 3.76, radius: 0.3, bendX: 0.24, bendZ: 0.17 },
    { x: 3.18, y: 5.76, z: 1.58, length: 4.24, radius: 0.39, bendX: 0.2, bendZ: 0.1 },
    { x: 4.26, y: 5.5, z: 1.28, length: 2.98, radius: 0.29, bendX: 0.3, bendZ: 0.14 },
  ] as const;
  icicles.forEach((icicle, index) => {
    addMesh(group, `icefall-icicle-${index}`, createIcicleGeometry(
      icicle.length,
      icicle.radius,
      icicle.bendX,
      icicle.bendZ,
      index * 0.41,
    ), index % 4 === 1 ? icefallWhite : index % 3 === 0 ? paleIce : luminousIce, {
      position: [icicle.x, icicle.y, icicle.z],
    });
  });

  // Short rear rime gives the uphill/back view an icy, asymmetric outline
  // rather than an anonymous row of dark boulders.
  for (const [index, rime] of [
    { x: -3.15, y: 5.72, length: 2.18, radius: 0.28 },
    { x: 0.72, y: 6.15, length: 2.62, radius: 0.32 },
    { x: 3.42, y: 5.42, length: 1.82, radius: 0.25 },
  ].entries()) {
    addMesh(group, `icefall-rear-rime-${index}`, createIcicleGeometry(
      rime.length,
      rime.radius,
      index * 0.16 - 0.14,
      -0.15,
      index * 0.62,
    ), index === 1 ? icefallWhite : luminousIce, {
      position: [rime.x, rime.y, -2.16],
    });
  }

  for (const [index, snow] of [
    { x: -3.48, y: 6.91, z: 0.6, sx: 1.5 },
    { x: -1.55, y: 7.18, z: 0.74, sx: 1.24 },
    { x: 0.42, y: 7.36, z: 0.72, sx: 1.48 },
    { x: 2.4, y: 6.93, z: 0.65, sx: 1.3 },
    { x: 3.78, y: 6.61, z: 0.56, sx: 1.08 },
  ].entries()) {
    addMesh(group, `icefall-snow-lip-${index}`, new THREE.IcosahedronGeometry(0.82, 1), glacierSnow, {
      position: [snow.x, snow.y, snow.z],
      scale: [snow.sx, 0.26, 1.12],
      rotation: [0, index * 0.43, index % 2 ? 0.05 : -0.04],
    });
  }
  addMesh(group, 'icefall-base-drift', new THREE.IcosahedronGeometry(1, 1), glacierSnow, {
    position: [0.18, 0.16, -0.22],
    scale: [4.75, 0.34, 1.82],
  });
  return finishModel('frozen-icefall', 'Frozen Hanging Icefall', 'ice-garden', group);
}

interface PulkSection {
  readonly x: number;
  readonly halfWidth: number;
  readonly bottom: number;
  readonly rim: number;
}

/** A shallow, open expedition hull with a pointed bow and kicked-up stern. */
function createPulkHullGeometry(): THREE.BufferGeometry {
  const sections: readonly PulkSection[] = [
    { x: -2.82, halfWidth: 0.12, bottom: 0.54, rim: 1.08 },
    { x: -2.22, halfWidth: 0.82, bottom: 0.42, rim: 1.02 },
    { x: 1.72, halfWidth: 0.98, bottom: 0.4, rim: 1 },
    { x: 2.5, halfWidth: 0.72, bottom: 0.58, rim: 1.2 },
  ];
  const positions: number[] = [];
  const pushTriangle = (a: VectorTuple, b: VectorTuple, c: VectorTuple): void => {
    positions.push(...a, ...b, ...c);
  };

  for (let index = 0; index < sections.length - 1; index += 1) {
    const a = sections[index]!;
    const b = sections[index + 1]!;
    const aBottomLeft: VectorTuple = [a.x, a.bottom, -a.halfWidth * 0.56];
    const aBottomRight: VectorTuple = [a.x, a.bottom, a.halfWidth * 0.56];
    const bBottomLeft: VectorTuple = [b.x, b.bottom, -b.halfWidth * 0.56];
    const bBottomRight: VectorTuple = [b.x, b.bottom, b.halfWidth * 0.56];
    const aRimLeft: VectorTuple = [a.x, a.rim, -a.halfWidth];
    const aRimRight: VectorTuple = [a.x, a.rim, a.halfWidth];
    const bRimLeft: VectorTuple = [b.x, b.rim, -b.halfWidth];
    const bRimRight: VectorTuple = [b.x, b.rim, b.halfWidth];

    pushTriangle(aBottomLeft, bBottomLeft, bBottomRight);
    pushTriangle(aBottomLeft, bBottomRight, aBottomRight);
    pushTriangle(aBottomLeft, aRimLeft, bRimLeft);
    pushTriangle(aBottomLeft, bRimLeft, bBottomLeft);
    pushTriangle(aBottomRight, bBottomRight, bRimRight);
    pushTriangle(aBottomRight, bRimRight, aRimRight);
  }

  const bow = sections[0]!;
  pushTriangle(
    [bow.x, bow.bottom, -bow.halfWidth * 0.56],
    [bow.x, bow.bottom, bow.halfWidth * 0.56],
    [bow.x, bow.rim, bow.halfWidth],
  );
  pushTriangle(
    [bow.x, bow.bottom, -bow.halfWidth * 0.56],
    [bow.x, bow.rim, bow.halfWidth],
    [bow.x, bow.rim, -bow.halfWidth],
  );
  const stern = sections[sections.length - 1]!;
  pushTriangle(
    [stern.x, stern.bottom, stern.halfWidth * 0.56],
    [stern.x, stern.bottom, -stern.halfWidth * 0.56],
    [stern.x, stern.rim, -stern.halfWidth],
  );
  pushTriangle(
    [stern.x, stern.bottom, stern.halfWidth * 0.56],
    [stern.x, stern.rim, -stern.halfWidth],
    [stern.x, stern.rim, stern.halfWidth],
  );

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function createSledRunnerGeometry(side: number): THREE.BufferGeometry {
  const curve = new THREE.CatmullRomCurve3([
    new THREE.Vector3(-2.72, 0.42, side * 0.72),
    new THREE.Vector3(-2.35, 0.12, side * 0.76),
    new THREE.Vector3(-1.55, 0.07, side * 0.78),
    new THREE.Vector3(1.55, 0.07, side * 0.8),
    new THREE.Vector3(2.3, 0.16, side * 0.75),
    new THREE.Vector3(2.68, 0.52, side * 0.65),
  ]);
  return new THREE.TubeGeometry(curve, 10, 0.075, 5, false);
}

function createSurveyFlagGeometry(): THREE.BufferGeometry {
  const positions = new THREE.Float32BufferAttribute([
    0, 0, 0,
    0, -0.62, 0,
    0.48, -0.08, 0.08,
    0.48, -0.08, 0.08,
    0, -0.62, 0,
    0.48, -0.55, 0.08,
    0.48, -0.08, 0.08,
    0.48, -0.55, 0.08,
    1.02, -0.2, -0.07,
    1.02, -0.2, -0.07,
    0.48, -0.55, 0.08,
    0.92, -0.48, -0.07,
  ], 3);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', positions);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

/** An abandoned glacier-mapping pulk slowly being swallowed by blue ice. */
export function createIceboundSurveySledModel(): THREE.Group {
  const group = new THREE.Group();
  addMesh(group, 'survey-sled-hull', createPulkHullGeometry(), sledCoral);
  addMesh(group, 'survey-sled-left-runner', createSledRunnerGeometry(-1), sledSteel);
  addMesh(group, 'survey-sled-right-runner', createSledRunnerGeometry(1), sledSteel);

  for (const [index, x] of [-1.7, 0.85, 2.05].entries()) {
    for (const side of [-1, 1]) {
      addBeam(
        group,
        `survey-runner-strut-${index}-${side}`,
        [x, 0.09, side * 0.78],
        [x, 0.58 + index * 0.03, side * 0.53],
        0.055,
        sledSteel,
        0.045,
        5,
      );
    }
  }

  addBeam(group, 'survey-left-tow-bar', [-2.46, 0.76, -0.48], [-4.02, 0.47, 0], 0.065, sledSteel, 0.05, 6);
  addBeam(group, 'survey-right-tow-bar', [-2.46, 0.76, 0.48], [-4.02, 0.47, 0], 0.065, sledSteel, 0.05, 6);
  addMesh(group, 'survey-tow-ring', new THREE.TorusGeometry(0.24, 0.055, 5, 10), sledSteel, {
    position: [-4.13, 0.46, 0],
    rotation: [0, 0, Math.PI / 2],
  });

  addMesh(group, 'survey-cargo-deck', new THREE.BoxGeometry(3.82, 0.12, 1.35), surveyWoodDark, {
    position: [0.18, 0.73, 0],
  });
  addMesh(group, 'survey-map-crate', new THREE.BoxGeometry(1.75, 1.12, 1.22), surveyWood, {
    position: [-0.42, 1.35, 0],
    rotation: [0, -0.08, 0.025],
  });
  for (const [index, x] of [-1.02, 0.18].entries()) {
    addBeam(group, `survey-crate-side-slat-${index}`, [x, 0.82, -0.64], [x, 1.9, -0.64], 0.055, surveyWoodDark, 0.055, 4);
    addBeam(group, `survey-crate-lashing-${index}`, [x, 1.95, -0.72], [x, 1.95, 0.72], 0.04, surveyRope, 0.04, 5);
  }

  // The pale route board and blue traverse line make the relic readable as
  // survey equipment—especially from the required top and angled studies.
  addMesh(group, 'survey-route-board', new THREE.BoxGeometry(1.42, 0.07, 0.94), mapPaper, {
    position: [-0.42, 1.98, 0],
    rotation: [0, -0.08, 0.025],
  });
  addBeam(group, 'survey-map-route-a', [-0.98, 2.03, -0.25], [-0.46, 2.05, 0.18], 0.026, mapInk, 0.026, 5);
  addBeam(group, 'survey-map-route-b', [-0.46, 2.05, 0.18], [0.08, 2.06, -0.14], 0.026, mapInk, 0.026, 5);
  for (const [index, point] of ([[-0.98, -0.25], [-0.46, 0.18], [0.08, -0.14]] as const).entries()) {
    addMesh(group, `survey-map-waypoint-${index}`, new THREE.CylinderGeometry(0.065, 0.065, 0.04, 6), sledCoral, {
      position: [point[0], 2.06, point[1]],
    });
  }

  addMesh(group, 'survey-canvas-roll', new THREE.CylinderGeometry(0.42, 0.42, 1.18, 7), surveyCanvas, {
    position: [1.45, 1.23, 0.02],
    rotation: [Math.PI / 2, 0, 0],
  });
  for (const z of [-0.42, 0.42]) {
    addMesh(group, `survey-roll-lashing-${z}`, new THREE.TorusGeometry(0.43, 0.035, 4, 8), surveyRope, {
      position: [1.45, 1.23, z],
      rotation: [Math.PI / 2, 0, 0],
    });
  }

  addBeam(group, 'survey-signal-pole', [2.02, 0.74, -0.48], [2.34, 4.46, -0.55], 0.065, sledSteel, 0.035, 6);
  addMesh(group, 'survey-signal-flag', createSurveyFlagGeometry(), sledCoral, {
    position: [2.31, 4.25, -0.54],
    rotation: [0.02, -0.08, -0.08],
    scale: 1.18,
  });
  addMesh(group, 'survey-pole-joint', new THREE.DodecahedronGeometry(0.15, 0), sledSteel, {
    position: [2.02, 0.74, -0.48],
  });

  // Low wind-polished chunks overlap the rear runner and hull, so the sled
  // looks genuinely immobilized rather than merely parked on a snow base.
  for (const [index, crust] of [
    { x: 1.48, y: 0.25, z: -0.74, sx: 1.28, sy: 0.48, sz: 0.78, ry: 0.18 },
    { x: 2.2, y: 0.46, z: -0.4, sx: 1.05, sy: 0.76, sz: 0.84, ry: -0.24 },
    { x: 2.76, y: 0.24, z: 0.08, sx: 0.9, sy: 0.42, sz: 0.94, ry: 0.36 },
  ].entries()) {
    addMesh(group, `survey-capturing-ice-${index}`, new THREE.IcosahedronGeometry(0.72, 0), index === 1 ? deepIce : paleIce, {
      position: [crust.x, crust.y, crust.z],
      scale: [crust.sx, crust.sy, crust.sz],
      rotation: [0, crust.ry, index % 2 ? 0.1 : -0.06],
    });
  }
  addMesh(group, 'survey-sled-foot-drift', new THREE.IcosahedronGeometry(1, 1), glacierSnow, {
    position: [0.2, 0.04, 0.18],
    scale: [3.25, 0.16, 1.18],
    rotation: [0, -0.12, 0],
  });

  return finishModel('icebound-survey-sled', 'Icebound Survey Sled', 'ice-garden', group);
}

interface ErmineBodyStation {
  readonly position: VectorTuple;
  readonly radius: number;
}

/** A tapered faceted skin following a loose curve without bead-like joints. */
function createErmineBodyGeometry(
  stations: readonly ErmineBodyStation[],
  sides = 7,
): THREE.BufferGeometry {
  const positions: number[] = [];
  const indices: number[] = [];
  const up = new THREE.Vector3(0, 1, 0);

  stations.forEach((station, stationIndex) => {
    const previous = new THREE.Vector3(...stations[Math.max(0, stationIndex - 1)]!.position);
    const next = new THREE.Vector3(...stations[Math.min(stations.length - 1, stationIndex + 1)]!.position);
    const tangent = next.sub(previous).normalize();
    const side = new THREE.Vector3().crossVectors(tangent, up).normalize();
    const normal = new THREE.Vector3().crossVectors(side, tangent).normalize();
    const center = new THREE.Vector3(...station.position);
    for (let sideIndex = 0; sideIndex < sides; sideIndex += 1) {
      const angle = sideIndex / sides * Math.PI * 2 + stationIndex * 0.12;
      const point = center.clone()
        .addScaledVector(normal, Math.cos(angle) * station.radius)
        .addScaledVector(side, Math.sin(angle) * station.radius);
      positions.push(point.x, point.y, point.z);
    }
  });

  for (let station = 0; station < stations.length - 1; station += 1) {
    for (let side = 0; side < sides; side += 1) {
      const nextSide = (side + 1) % sides;
      const start = station * sides + side;
      const startNext = station * sides + nextSide;
      const end = (station + 1) * sides + side;
      const endNext = (station + 1) * sides + nextSide;
      indices.push(start, end, startNext, startNext, end, endNext);
    }
  }

  const startCenter = positions.length / 3;
  positions.push(...stations[0]!.position);
  const endCenter = positions.length / 3;
  positions.push(...stations[stations.length - 1]!.position);
  for (let side = 0; side < sides; side += 1) {
    const nextSide = (side + 1) % sides;
    indices.push(startCenter, nextSide, side);
    const lastRing = (stations.length - 1) * sides;
    indices.push(endCenter, lastRing + side, lastRing + nextSide);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function createSkidGroove(points: readonly VectorTuple[]): THREE.BufferGeometry {
  return new THREE.TubeGeometry(
    new THREE.CatmullRomCurve3(points.map((point) => new THREE.Vector3(...point))),
    12,
    0.036,
    5,
    false,
  );
}

/** A winter ermine caught belly-skidding away with a surveyor's mitten. */
export function createMittenSkiddingErmineModel(): THREE.Group {
  const group = new THREE.Group();

  addMesh(group, 'ermine-curved-body', createErmineBodyGeometry([
    { position: [-0.92, 0.58, -0.18], radius: 0.4 },
    { position: [-0.46, 0.66, -0.16], radius: 0.57 },
    { position: [0.08, 0.72, -0.04], radius: 0.6 },
    { position: [0.58, 0.77, 0.17], radius: 0.51 },
    { position: [0.92, 0.87, 0.38], radius: 0.37 },
    { position: [1.05, 0.95, 0.47], radius: 0.29 },
  ]), ermineWhite);
  addMesh(group, 'ermine-blue-shadow-belly', new THREE.IcosahedronGeometry(0.56, 1), ermineShade, {
    position: [-0.02, 0.35, -0.01],
    scale: [1.58, 0.4, 0.76],
    rotation: [0.02, -0.12, 0.02],
  });

  // The tail hooks against the direction of travel. A separate dark final
  // skin keeps its classic winter tip visible from top and rear studies.
  addMesh(group, 'ermine-white-tail', createErmineBodyGeometry([
    { position: [-0.88, 0.62, -0.17], radius: 0.3 },
    { position: [-1.28, 0.82, -0.48], radius: 0.28 },
    { position: [-1.68, 0.91, -0.55], radius: 0.23 },
    { position: [-1.98, 0.83, -0.34], radius: 0.18 },
    { position: [-2.12, 0.68, -0.07], radius: 0.14 },
  ], 6), ermineWhite);
  addMesh(group, 'ermine-black-tail-tip', createErmineBodyGeometry([
    { position: [-2.12, 0.68, -0.07], radius: 0.145 },
    { position: [-2.25, 0.51, 0.17], radius: 0.09 },
    { position: [-2.3, 0.4, 0.34], radius: 0.03 },
  ], 6), ermineCharcoal);

  // Its head turns back toward the viewer while the body keeps sliding +X.
  addMesh(group, 'ermine-head', new THREE.IcosahedronGeometry(0.42, 1), ermineWhite, {
    position: [1.08, 1.05, 0.53],
    scale: [1, 1.02, 1.12],
    rotation: [0.08, -0.18, -0.05],
  });
  addMesh(group, 'ermine-muzzle', new THREE.IcosahedronGeometry(0.24, 0), ermineShade, {
    position: [1.16, 0.93, 0.88],
    scale: [0.86, 0.72, 1.18],
    rotation: [0.08, 0.05, 0],
  });
  addMesh(group, 'ermine-nose', new THREE.DodecahedronGeometry(0.09, 0), ermineCharcoal, {
    position: [1.18, 0.93, 1.11],
  });

  for (const [index, ear] of [
    { x: 0.86, y: 1.35, z: 0.44, rotation: -0.18 },
    { x: 1.25, y: 1.31, z: 0.49, rotation: 0.17 },
  ].entries()) {
    addMesh(group, `ermine-round-ear-${index}`, new THREE.IcosahedronGeometry(0.22, 0), ermineWhite, {
      position: [ear.x, ear.y, ear.z],
      rotation: [0.08, 0, ear.rotation],
      scale: [0.92, 1, 0.64],
    });
    addMesh(group, `ermine-inner-ear-${index}`, new THREE.IcosahedronGeometry(0.12, 0), ermineInnerEar, {
      position: [ear.x, ear.y + 0.005, ear.z + 0.115],
      rotation: [0.08, 0, ear.rotation],
      scale: [0.82, 0.88, 0.26],
    });
  }
  addMesh(group, 'ermine-near-eye', new THREE.DodecahedronGeometry(0.07, 0), ermineCharcoal, {
    position: [1.03, 1.13, 0.94],
  });
  addMesh(group, 'ermine-far-eye', new THREE.DodecahedronGeometry(0.058, 0), ermineCharcoal, {
    position: [1.34, 1.11, 0.65],
  });
  for (const [index, whisker] of [
    { start: [1.14, 0.96, 1.03] as VectorTuple, end: [0.82, 1, 1.34] as VectorTuple },
    { start: [1.15, 0.92, 1.04] as VectorTuple, end: [0.8, 0.84, 1.32] as VectorTuple },
    { start: [1.22, 0.96, 1.03] as VectorTuple, end: [1.51, 1.02, 1.3] as VectorTuple },
    { start: [1.22, 0.91, 1.04] as VectorTuple, end: [1.53, 0.82, 1.27] as VectorTuple },
  ].entries()) {
    addBeam(group, `ermine-whisker-${index}`, whisker.start, whisker.end, 0.012, ermineCharcoal, 0.006, 4);
  }

  const paws = [
    { name: 'front-near', start: [0.62, 0.66, 0.27] as VectorTuple, end: [1.06, 0.34, 0.93] as VectorTuple, rotation: -0.12 },
    { name: 'front-far', start: [0.5, 0.65, -0.2] as VectorTuple, end: [1.04, 0.2, -0.72] as VectorTuple, rotation: 0.14 },
    { name: 'rear-near', start: [-0.58, 0.58, 0.16] as VectorTuple, end: [-0.93, 0.16, 0.8] as VectorTuple, rotation: 0.18 },
    // One airborne rear paw and the adjacent chip burst freeze the skid at a
    // more mischievous instant than a symmetrical four-paw sprawl.
    { name: 'rear-kicked', start: [-0.65, 0.59, -0.23] as VectorTuple, end: [-1.13, 0.86, -0.77] as VectorTuple, rotation: -0.34 },
  ] as const;
  paws.forEach((paw) => {
    addBeam(group, `ermine-${paw.name}-leg`, paw.start, paw.end, 0.145, ermineWhite, 0.1, 6);
    addMesh(group, `ermine-${paw.name}-paw`, new THREE.IcosahedronGeometry(0.19, 0), ermineShade, {
      position: paw.end,
      scale: [1.45, 0.45, 0.72],
      rotation: [0, paw.rotation, 0],
    });
  });

  // A chunky palm, cuff, and thumb survive runtime scale better than a flat
  // mitten cutout and repeat the survey sled's warm coral signal color.
  addMesh(group, 'ermine-stolen-mitten-palm', new THREE.IcosahedronGeometry(0.26, 0), stolenMitten, {
    position: [1.23, 0.69, 1.22],
    scale: [0.76, 1.05, 0.42],
    rotation: [0.22, 0.08, -0.22],
  });
  addMesh(group, 'ermine-stolen-mitten-thumb', new THREE.IcosahedronGeometry(0.13, 0), stolenMitten, {
    position: [1.03, 0.66, 1.22],
    scale: [1.02, 0.62, 0.58],
    rotation: [0, 0.18, 0.58],
  });
  addMesh(group, 'ermine-stolen-mitten-cuff', new THREE.BoxGeometry(0.44, 0.18, 0.25), stolenMitten, {
    position: [1.3, 0.48, 1.19],
    scale: 0.85,
    rotation: [0.22, 0.08, -0.12],
  });

  addMesh(group, 'ermine-curved-skid-groove', createSkidGroove([
    [-2.22, 0.04, 0.36],
    [-1.82, 0.04, 0.3],
    [-1.38, 0.04, 0.4],
    [-0.98, 0.04, 0.62],
  ]), skidBlue);
  addMesh(group, 'ermine-skid-fork', createSkidGroove([
    [-1.45, 0.041, 0.4],
    [-1.18, 0.041, 0.17],
    [-0.95, 0.041, -0.12],
  ]), skidBlue);

  for (const [index, chip] of [
    { position: [-1.2, 0.84, -0.78] as VectorTuple, scale: [0.11, 0.23, 0.1] as VectorTuple },
    { position: [-1.42, 0.62, -0.84] as VectorTuple, scale: [0.08, 0.16, 0.08] as VectorTuple },
    { position: [-0.98, 0.55, -0.9] as VectorTuple, scale: [0.07, 0.13, 0.07] as VectorTuple },
  ].entries()) {
    addMesh(group, `ermine-kicked-ice-chip-${index}`, new THREE.OctahedronGeometry(1, 0), paleIce, {
      position: chip.position,
      scale: chip.scale,
      rotation: [index * 0.31, index * 0.47, index % 2 ? 0.22 : -0.18],
    });
  }

  return finishModel('mitten-skidding-ermine', 'Mitten-Skidding Ermine', 'ice-garden', group);
}
